import { useEffect, useState } from "react";
import { useLocation } from "react-router-dom";
import { toast } from "sonner";
import { CheckCircle, Circle, MagnifyingGlass, Truck, WashingMachine, Package, House, CalendarCheck, CreditCard, FileArrowDown } from "@phosphor-icons/react";
import api, { fmtErr } from "@/lib/api";

const statusIcons = { "Booked": CalendarCheck, "Picked Up": Truck, "Washing": WashingMachine, "Ready": Package, "Out for Delivery": Truck, "Delivered": House };

export default function Track() {
  const location = useLocation();
  const [orderNumber, setOrderNumber] = useState(location.state?.order_number || "");
  const [phone, setPhone] = useState(location.state?.phone || "");
  const [booking, setBooking] = useState(null);
  const [loading, setLoading] = useState(false);
  const [paying, setPaying] = useState(false);

  useEffect(() => { document.title = "Track Your Order | Zaekids Laundromat NSW"; }, []);

  const payNow = async () => {
    setPaying(true);
    try {
      const { data } = await api.post("/payments/checkout", {
        order_number: booking.order_number, phone, origin_url: window.location.origin,
      });
      window.location.href = data.checkout_url;
    } catch (err) {
      toast.error(fmtErr(err));
      setPaying(false);
    }
  };

  const downloadInvoice = async () => {
    try {
      const res = await api.get("/track/invoice", { params: { order_number: booking.order_number, phone }, responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url; a.download = `invoice-${booking.order_number}.pdf`; a.click();
      URL.revokeObjectURL(url);
    } catch (e) { toast.error(fmtErr(e)); }
  };

  const search = async (e) => {
    e?.preventDefault();
    setLoading(true);
    setBooking(null);
    try {
      const { data } = await api.get("/track", { params: { order_number: orderNumber, phone } });
      setBooking(data);
    } catch (err) {
      toast.error(fmtErr(err));
    } finally {
      setLoading(false);
    }
  };

  const currentIdx = booking ? booking.all_statuses.indexOf(booking.status) : -1;
  const historyMap = booking ? Object.fromEntries(booking.status_history.map((h) => [h.status, h.at])) : {};
  const input = "w-full rounded-2xl border-2 border-brand-green/15 bg-white px-4 py-3 text-sm font-semibold outline-none transition-colors duration-200 focus:border-brand-green focus:ring-4 focus:ring-brand-green/10";

  return (
    <div data-testid="track-page" className="mx-auto max-w-2xl px-4 py-16 sm:px-6">
      <div className="text-center">
        <span className="font-script text-3xl font-bold text-brand-yellow-dark">Where's my laundry?</span>
        <h1 className="mt-2 text-4xl font-black tracking-tight text-brand-green sm:text-5xl">Track Your Order</h1>
        <p className="mt-4 text-slate-600">Enter your order number and the phone number you booked with.</p>
      </div>

      <form onSubmit={search} className="mt-10 rounded-3xl bg-white p-8 shadow-[0_8px_30px_rgb(27,94,32,0.08)]">
        <label className="mb-1 block text-sm font-extrabold text-brand-green">Order number</label>
        <input data-testid="track-order-input" className={input} required value={orderNumber}
          onChange={(e) => setOrderNumber(e.target.value)} placeholder="ZK-123456" />
        <label className="mb-1 mt-4 block text-sm font-extrabold text-brand-green">Phone number</label>
        <input data-testid="track-phone-input" className={input} required value={phone}
          onChange={(e) => setPhone(e.target.value)} placeholder="04XX XXX XXX" />
        <button type="submit" disabled={loading} data-testid="track-submit-btn"
          className="btn-bounce mt-6 flex w-full items-center justify-center gap-2 rounded-full bg-brand-green px-8 py-4 font-extrabold text-white disabled:opacity-60">
          <MagnifyingGlass size={20} weight="bold" /> {loading ? "Searching…" : "Track My Order"}
        </button>
      </form>

      {booking && (
        <div data-testid="track-result" className="mt-10 rounded-3xl bg-white p-8 shadow-[0_8px_30px_rgb(27,94,32,0.08)]">
          <div className="flex flex-wrap items-center justify-between gap-2">
            <h2 className="text-xl font-extrabold text-brand-green">Order {booking.order_number}</h2>
            <span data-testid="track-current-status" className="rounded-full bg-brand-yellow px-4 py-1.5 text-sm font-extrabold text-brand-green">{booking.status}</span>
          </div>
          <p className="mt-2 text-sm text-slate-500">Pick up: {booking.pickup_date} · {booking.time_slot} · Total ${booking.pricing.total.toFixed(2)}</p>
          {booking.payment_status === "paid" ? (
            <span data-testid="track-paid-badge" className="mt-3 inline-flex items-center gap-2 rounded-full bg-soft-green px-4 py-1.5 text-sm font-extrabold text-brand-green">
              <CheckCircle size={18} weight="fill" /> Paid — thank you!
            </span>
          ) : booking.payment_status === "refund_due" ? (
            <span data-testid="track-refund-badge" className="mt-3 inline-flex items-center gap-2 rounded-full bg-soft-yellow px-4 py-1.5 text-sm font-extrabold text-brand-green">
              <CheckCircle size={18} weight="fill" /> Paid ${(booking.amount_paid || 0).toFixed(2)} — refund of ${((booking.amount_paid || 0) - booking.pricing.total).toFixed(2)} due; we'll sort it on delivery.
            </span>
          ) : (
            <button data-testid="track-pay-btn" onClick={payNow} disabled={paying}
              className="btn-bounce mt-3 flex items-center gap-2 rounded-full bg-brand-yellow px-6 py-3 font-extrabold text-brand-green disabled:opacity-60">
              <CreditCard size={18} weight="duotone" /> {paying ? "Redirecting to payment…"
                : booking.payment_status === "balance_due"
                  ? `Pay Balance — $${(booking.pricing.total - (booking.amount_paid || 0)).toFixed(2)}`
                  : `Pay Now — $${booking.pricing.total.toFixed(2)}`}
            </button>
          )}
          <button data-testid="track-invoice-btn" onClick={downloadInvoice}
            className="mt-3 sm:ml-3 inline-flex items-center gap-2 rounded-full border-2 border-brand-green/25 px-5 py-2.5 text-sm font-extrabold text-brand-green hover:bg-soft-green">
            <FileArrowDown size={16} weight="bold" /> Download {["paid", "refund_due"].includes(booking.payment_status) ? "receipt" : "invoice"}
          </button>
          <div className="mt-8 space-y-0">
            {booking.all_statuses.map((s, i) => {
              const Icon = statusIcons[s] || Circle;
              const done = i <= currentIdx;
              return (
                <div key={s} className="flex gap-4">
                  <div className="flex flex-col items-center">
                    <span className={`flex h-11 w-11 items-center justify-center rounded-full ${done ? "bg-brand-green text-white" : "bg-slate-100 text-slate-400"}`}>
                      {done ? <CheckCircle size={24} weight="fill" /> : <Icon size={22} weight="duotone" />}
                    </span>
                    {i < booking.all_statuses.length - 1 && <span className={`h-8 w-1 rounded ${i < currentIdx ? "bg-brand-green" : "bg-slate-200"}`} />}
                  </div>
                  <div className="pb-2">
                    <p className={`font-extrabold ${done ? "text-brand-green" : "text-slate-400"}`}>{s}</p>
                    {historyMap[s] && <p className="text-xs text-slate-400">{new Date(historyMap[s]).toLocaleString("en-AU", { dateStyle: "medium", timeStyle: "short" })}</p>}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}
